import { useEffect, useState } from 'react'
import { Field } from '@/components/common/Field'
import { Modal } from '@/components/common/Modal'
import { inputClass, primaryButtonClass } from '@/components/common/formStyles'
import { useForkStrategy } from '@/lib/useStrategy'

interface ForkStrategyModalProps {
  open: boolean
  onClose: () => void
  parentId: number
  parentVersion: string
}

/**
 * Erzeugt eine neue Draft-Version durch Fork der angegebenen Strategy.
 * Der Edit-Workflow für die Draft kommt mit dem Phase-1b-Wizard — bis
 * dahin ist die neue Version in `/strategy/history` sichtbar.
 */
export function ForkStrategyModal({
  open,
  onClose,
  parentId,
  parentVersion,
}: ForkStrategyModalProps) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const fork = useForkStrategy()

  useEffect(() => {
    if (!open) return
    setReason('')
    setError(null)
  }, [open])

  const handleSubmit = async () => {
    setError(null)
    try {
      const trimmed = reason.trim()
      const result = await fork.mutateAsync({
        id: parentId,
        reason: trimmed === '' ? undefined : trimmed,
      })
      onClose()
      // Hinweis: aktive Strategy bleibt aktiv. Die neue Draft v.{result.version}
      // ist in /strategy/history einsehbar. Edit-UI folgt mit Phase-1b-Wizard.
      window.alert(
        `Draft v${result.version} erstellt (${result.rules.length} Regeln kopiert). ` +
          'Sichtbar in der Versions-Historie.',
      )
    } catch (err) {
      setError((err as Error).message)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={`Fork v${parentVersion}`}>
      <div className="flex flex-col gap-5">
        <p className="font-mono text-xs text-muted">
          Erzeugt eine neue Draft-Version mit Minor-Bump und kopierten Regeln. Die aktuell aktive
          Strategy bleibt unverändert. Editieren der Draft folgt mit dem Onboarding-Wizard (Phase
          1b).
        </p>

        <Field label="Begründung (optional)">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="z.B. Cluster-Limits angepasst, Cash-Quote erhöht …"
            rows={3}
            className={inputClass}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-loss">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="border border-hairline px-4 py-2 font-mono text-xs text-muted transition-colors hover:text-bernstein"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={fork.isPending}
            className={primaryButtonClass}
          >
            {fork.isPending ? 'Forke …' : 'Fork erstellen'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
